import React, { useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import {
  Alert,
  App,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  UserOutlined,
  HistoryOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { COMPETENCY_VIEW_ANY, useCompetencyCan } from '@/hooks/useCompetency';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import EmployeeUaeDocumentsCard from '@/components/hr/EmployeeUaeDocumentsCard';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';
import { apiOriginForStaticAssets } from '@/config/apiBaseUrl';
import employeeService, {
  employeeKeys,
  type EmergencyContactRecord,
  type EmployeeLifecycleEventRecord,
  type EmployeePerformanceRecord,
  type EmployeeQualificationRecord,
  type EmployeeShiftAssignmentRecord,
  type LeaveBalanceRecord,
  type ProfileChangeRequestRecord,
  type SalaryComponentRecord,
} from '@/services/employee.service';
import hrmsService, { hrmsKeys, type AllocationRow } from '@/services/hrms.service';
import departmentService from '@/services/department.service';
import companyService from '@/services/company.service';
import { useAuthStore } from '@/store/authStore';
import { ALLOCATION_STATUS_COLOR, buildLinearWorkflow, hrmsStatusColor } from './shared/hrmsStatus';
import { promptHandoverIfNeeded } from '@/components/moc/handoverPrompt';

const { Text } = Typography;

const EXIT_TYPE_OPTIONS = [
  { value: 'Resignation', label: 'Resignation' },
  { value: 'Termination', label: 'Termination' },
  { value: 'EndOfContract', label: 'End of contract' },
  { value: 'Retirement', label: 'Retirement' },
  { value: 'Other', label: 'Other' },
];

const fmtDate = (d?: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '—');
const toApiDate = (v: unknown): string | undefined => {
  if (!v) return undefined;
  if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
  const p = dayjs(v as string);
  return p.isValid() ? p.format('YYYY-MM-DD') : undefined;
};

function daysUntil(dateStr?: string): number | null {
  if (!dateStr) return null;
  const d = dayjs(dateStr);
  if (!d.isValid()) return null;
  return d.diff(dayjs(), 'day');
}

const QUAL_TYPES = ['Education', 'Licence', 'Certification', 'Competency', 'Skill'] as const;
const PERF_TYPES = ['Goal', 'Appraisal', 'Training', 'Succession'] as const;
const LIFECYCLE_TYPES = [
  'Transfer',
  'Promotion',
  'Demotion',
  'Disciplinary',
  'Secondment',
  'Resignation',
  'Termination',
  'Rehire',
  'Confirmation',
  'Other',
] as const;

const EmployeeProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const canViewCompetency = useCompetencyCan(...COMPETENCY_VIEW_ANY);
  const canEdit =
    Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.employee.edit'));
  const canViewAllocations =
    Boolean(user?.is_super_admin) ||
    Boolean(user?.permissions?.some((p) => ['hr.allocation.manage', 'hr.view', 'hr.employee.edit'].includes(p)));
  const { indiaPack } = useHrmsPolicy();

  const [ecModalOpen, setEcModalOpen] = useState(false);
  const [ecEditing, setEcEditing] = useState<EmergencyContactRecord | null>(null);
  const [ecForm] = Form.useForm();
  const [lbModalOpen, setLbModalOpen] = useState(false);
  const [lbForm] = Form.useForm();

  const [qualModalOpen, setQualModalOpen] = useState(false);
  const [qualEditing, setQualEditing] = useState<EmployeeQualificationRecord | null>(null);
  const [qualForm] = Form.useForm();

  const [perfModalOpen, setPerfModalOpen] = useState(false);
  const [perfEditing, setPerfEditing] = useState<EmployeePerformanceRecord | null>(null);
  const [perfForm] = Form.useForm();

  const [lifeModalOpen, setLifeModalOpen] = useState(false);
  const [lifeEditing, setLifeEditing] = useState<EmployeeLifecycleEventRecord | null>(null);
  const [lifeForm] = Form.useForm();

  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [shiftForm] = Form.useForm();
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [exitForm] = Form.useForm();
  const [lifecycleBusy, setLifecycleBusy] = useState<string | null>(null);

  const { data: employee, isLoading } = useQuery({
    queryKey: employeeKeys.detail(id!),
    queryFn: () => employeeService.getById(id!),
    enabled: !!id,
  });

  const { data: emergency = [] } = useQuery({
    queryKey: employeeKeys.emergency(id!),
    queryFn: () => employeeService.listEmergencyContacts(id!),
    enabled: !!id,
  });

  const { data: leaveBalances = [] } = useQuery({
    queryKey: employeeKeys.leaveBalances(id!),
    queryFn: () => employeeService.listLeaveBalances(id!),
    enabled: !!id,
  });

  const { data: salaryComponents = [] } = useQuery({
    queryKey: employeeKeys.salaryComponents(id!),
    queryFn: () => employeeService.listSalaryComponents(id!),
    enabled: !!id,
  });

  const { data: salaryRevisions = [] } = useQuery({
    queryKey: hrmsKeys.salaryRevisions({ employee_id: id }),
    queryFn: () => hrmsService.listSalaryRevisions({ employee_id: id }),
    enabled: !!id,
  });

  const { data: profileRequests = [] } = useQuery({
    queryKey: employeeKeys.profileRequests(id!),
    queryFn: () => employeeService.listProfileChangeRequestsForEmployee(id!),
    enabled: !!id,
  });

  const { data: contracts = [] } = useQuery({
    queryKey: hrmsKeys.contracts(id),
    queryFn: () => hrmsService.listContracts({ employee_id: id }),
    enabled: !!id,
  });

  const { data: leaveApps = [] } = useQuery({
    queryKey: hrmsKeys.leaveApplications({ employee_id: id }),
    queryFn: () => hrmsService.listLeaveApplications({ employee_id: id }),
    enabled: !!id,
  });

  const { data: qualifications = [] } = useQuery({
    queryKey: employeeKeys.qualifications(id!),
    queryFn: () => employeeService.listQualifications(id!),
    enabled: !!id,
  });

  const { data: performance = [] } = useQuery({
    queryKey: employeeKeys.performance(id!),
    queryFn: () => employeeService.listPerformance(id!),
    enabled: !!id,
  });

  const { data: lifecycle = [] } = useQuery({
    queryKey: employeeKeys.lifecycle(id!),
    queryFn: () => employeeService.listLifecycle(id!),
    enabled: !!id,
  });

  const allocationParams = { employee_id: id, status: 'all' };
  const { data: allocations = [] } = useQuery({
    queryKey: hrmsKeys.allocations(allocationParams),
    queryFn: () => hrmsService.listAllocations(allocationParams) as Promise<AllocationRow[]>,
    enabled: !!id && canViewAllocations,
  });

  const { data: shiftAssignments = [] } = useQuery({
    queryKey: employeeKeys.shiftAssignments(id!),
    queryFn: () => employeeService.listShiftAssignments(id!),
    enabled: !!id,
  });

  const { data: shifts = [] } = useQuery({
    queryKey: hrmsKeys.shifts(),
    queryFn: () => hrmsService.listShifts(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: holidayCalendars = [] } = useQuery({
    queryKey: hrmsKeys.holidayCalendars(),
    queryFn: () => hrmsService.listHolidayCalendars(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection', 'employee-profile'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: branches = [] } = useQuery({
    queryKey: ['company-branches', 'employee-profile'],
    queryFn: () => companyService.listBranches({ is_active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: employeesList = [] } = useQuery({
    queryKey: employeeKeys.list(),
    queryFn: () => employeeService.list(),
    staleTime: 60_000,
  });

  const shiftLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of shifts) {
      m.set(s.id, s.shift_name || s.shift_code || '—');
    }
    return m;
  }, [shifts]);

  const calendarLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of holidayCalendars) {
      m.set(
        c.id,
        `${c.calendar_name || 'Calendar'}${c.calendar_year != null ? ` (${c.calendar_year})` : ''}`
      );
    }
    return m;
  }, [holidayCalendars]);

  const departmentLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departments) {
      m.set(d.id, [d.department_code, d.department_name].filter(Boolean).join(' — ') || '—');
    }
    return m;
  }, [departments]);

  const { data: workLocations = [] } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
    staleTime: 5 * 60 * 1000,
  });
  const locationLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of workLocations as any[]) m.set(l.id, `${l.location_code} — ${l.location_name}`);
    return m;
  }, [workLocations]);
  const locationLabel = (locId?: string | null) =>
    locId ? locationLabelById.get(locId) || locId : '—';

  const branchLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of branches) {
      m.set(b.id, [b.code, b.name].filter(Boolean).join(' — ') || '—');
    }
    return m;
  }, [branches]);

  const employeeLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employeesList) {
      m.set(
        e.id,
        [e.employee_code, `${e.first_name} ${e.last_name}`.trim()].filter(Boolean).join(' — ') || '—'
      );
    }
    return m;
  }, [employeesList]);

  const photoMutation = useMutation({
    mutationFn: (file: File) => employeeService.uploadPhoto(id!, file),
    onSuccess: () => {
      message.success('Photo updated');
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id!) });
    },
    onError: () => message.error('Photo upload failed'),
  });

  const complianceItems = useMemo(() => {
    if (!employee) return [];
    const items = indiaPack
      ? [
          { label: 'Passport', value: employee.passport_expiry_date },
          { label: 'Insurance', value: employee.insurance_expiry },
        ]
      : [
          { label: 'Emirates ID', value: employee.emirates_id_expiry },
          { label: 'Passport', value: employee.passport_expiry_date },
          { label: 'Visa', value: employee.visa_expiry_date },
          { label: 'Labour card', value: employee.labour_card_expiry },
          { label: 'Insurance', value: employee.insurance_expiry },
          { label: 'Work permit', value: employee.work_permit_expiry_date },
        ];
    return items.map((p) => ({ ...p, days: daysUntil(p.value) })).filter((p) => p.value);
  }, [employee, indiaPack]);

  const workflow = useMemo(
    () =>
      buildLinearWorkflow(
        ['Onboarding', 'Active', 'On Leave', 'Resigned'],
        employee?.employment_status || 'Active'
      ),
    [employee?.employment_status]
  );

  const parseAddress = (json?: string) => {
    if (!json) return null;
    try {
      return JSON.parse(json) as { line1?: string; city?: string; country?: string };
    } catch {
      return null;
    }
  };

  const openEc = (row?: EmergencyContactRecord) => {
    setEcEditing(row ?? null);
    if (row) ecForm.setFieldsValue(row);
    else ecForm.resetFields();
    setEcModalOpen(true);
  };

  const saveEmergency = async () => {
    const v = await ecForm.validateFields();
    if (!id) return;
    try {
      if (ecEditing) await employeeService.updateEmergencyContact(id, ecEditing.id, v);
      else await employeeService.createEmergencyContact(id, v);
      message.success(ecEditing ? 'Contact updated' : 'Contact added');
      setEcModalOpen(false);
      queryClient.invalidateQueries({ queryKey: employeeKeys.emergency(id) });
    } catch {
      message.error('Could not save contact');
    }
  };

  const submitLeaveBalance = async () => {
    const v = await lbForm.validateFields();
    await employeeService.upsertLeaveBalance(id!, {
      leave_year: Number(v.leave_year),
      leave_type: v.leave_type,
      opening_balance: v.opening_balance,
      consumed: v.consumed,
      carried_forward: v.carried_forward,
    });
    message.success('Saved');
    setLbModalOpen(false);
    queryClient.invalidateQueries({ queryKey: employeeKeys.leaveBalances(id!) });
  };

  const openQual = (row?: EmployeeQualificationRecord) => {
    setQualEditing(row ?? null);
    if (row) {
      qualForm.setFieldsValue({
        ...row,
        issued_on: row.issued_on ? dayjs(row.issued_on) : undefined,
        expiry_date: row.expiry_date ? dayjs(row.expiry_date) : undefined,
      });
    } else {
      qualForm.resetFields();
      qualForm.setFieldsValue({ qual_type: 'Education', is_verified: false });
    }
    setQualModalOpen(true);
  };

  const saveQual = async () => {
    const v = await qualForm.validateFields();
    const body = {
      qual_type: v.qual_type,
      title: v.title,
      institution: v.institution || undefined,
      credential_number: v.credential_number || undefined,
      issued_on: toApiDate(v.issued_on),
      expiry_date: toApiDate(v.expiry_date),
      level_or_grade: v.level_or_grade || undefined,
      country: v.country || undefined,
      notes: v.notes || undefined,
      is_verified: Boolean(v.is_verified),
    };
    try {
      if (qualEditing) await employeeService.updateQualification(id!, qualEditing.id, body);
      else await employeeService.createQualification(id!, body as any);
      message.success(qualEditing ? 'Qualification updated' : 'Qualification added');
      setQualModalOpen(false);
      queryClient.invalidateQueries({ queryKey: employeeKeys.qualifications(id!) });
    } catch {
      message.error('Could not save qualification');
    }
  };

  const openPerf = (row?: EmployeePerformanceRecord) => {
    setPerfEditing(row ?? null);
    if (row) {
      perfForm.setFieldsValue({
        ...row,
        due_date: row.due_date ? dayjs(row.due_date) : undefined,
        completed_date: row.completed_date ? dayjs(row.completed_date) : undefined,
      });
    } else {
      perfForm.resetFields();
      perfForm.setFieldsValue({ record_type: 'Goal', status: 'Open', period_year: dayjs().year() });
    }
    setPerfModalOpen(true);
  };

  const savePerf = async () => {
    const v = await perfForm.validateFields();
    const body = {
      record_type: v.record_type,
      title: v.title,
      period_year: v.period_year != null ? Number(v.period_year) : undefined,
      period_label: v.period_label || undefined,
      status: v.status || 'Open',
      rating: v.rating || undefined,
      due_date: toApiDate(v.due_date),
      completed_date: toApiDate(v.completed_date),
      summary: v.summary || undefined,
    };
    try {
      if (perfEditing) await employeeService.updatePerformance(id!, perfEditing.id, body);
      else await employeeService.createPerformance(id!, body as any);
      message.success(perfEditing ? 'Record updated' : 'Record added');
      setPerfModalOpen(false);
      queryClient.invalidateQueries({ queryKey: employeeKeys.performance(id!) });
    } catch {
      message.error('Could not save performance record');
    }
  };

  const openLife = (row?: EmployeeLifecycleEventRecord) => {
    setLifeEditing(row ?? null);
    if (row) {
      lifeForm.setFieldsValue({
        ...row,
        event_date: row.event_date ? dayjs(row.event_date) : undefined,
        effective_from: row.effective_from ? dayjs(row.effective_from) : undefined,
        effective_to: row.effective_to ? dayjs(row.effective_to) : undefined,
      });
    } else {
      lifeForm.resetFields();
      lifeForm.setFieldsValue({ event_type: 'Other', status: 'Recorded', event_date: dayjs() });
    }
    setLifeModalOpen(true);
  };

  const saveLife = async () => {
    const v = await lifeForm.validateFields();
    const eventDate = toApiDate(v.event_date);
    if (!eventDate) {
      message.error('Event date is required');
      return;
    }
    const body = {
      event_type: v.event_type,
      event_date: eventDate,
      effective_from: toApiDate(v.effective_from),
      effective_to: toApiDate(v.effective_to),
      from_job_title: v.from_job_title || undefined,
      to_job_title: v.to_job_title || undefined,
      from_grade: v.from_grade || undefined,
      to_grade: v.to_grade || undefined,
      reason: v.reason || undefined,
      outcome: v.outcome || undefined,
      status: v.status || 'Recorded',
      document_ref: v.document_ref || undefined,
    };
    try {
      if (lifeEditing) await employeeService.updateLifecycle(id!, lifeEditing.id, body);
      else await employeeService.createLifecycle(id!, body as any);
      message.success(lifeEditing ? 'Event updated' : 'Event added');
      setLifeModalOpen(false);
      queryClient.invalidateQueries({ queryKey: employeeKeys.lifecycle(id!) });
    } catch {
      message.error('Could not save lifecycle event');
    }
  };

  const saveShiftAssignment = async () => {
    const v = await shiftForm.validateFields();
    const effectiveFrom = toApiDate(v.effective_from);
    if (!effectiveFrom) {
      message.error('Effective from is required');
      return;
    }
    try {
      await employeeService.createShiftAssignment(id!, {
        shift_id: v.shift_id,
        effective_from: effectiveFrom,
        effective_to: toApiDate(v.effective_to),
      });
      message.success('Shift assignment saved — overlapping assignments were adjusted');
      setShiftModalOpen(false);
      shiftForm.resetFields();
      queryClient.invalidateQueries({ queryKey: employeeKeys.shiftAssignments(id!) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.shiftOverview() });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not save shift assignment');
    }
  };

  const endShift = async (row: EmployeeShiftAssignmentRecord) => {
    try {
      await employeeService.endShiftAssignment(id!, row.id);
      message.success(dayjs(row.effective_from).isAfter(dayjs(), 'day') ? 'Upcoming assignment cancelled' : 'Assignment ended');
      queryClient.invalidateQueries({ queryKey: employeeKeys.shiftAssignments(id!) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.shiftOverview() });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not end assignment');
    }
  };

  if (!id) return null;

  if (isLoading || !employee) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Employee" />}>
        {id ? <AiPageContextSync module="hr" docType="Employee" docId={id} /> : null}
        <Text>{isLoading ? 'Loading…' : 'Employee not found'}</Text>
      </WorkspaceLayout>
    );
  }

  const serverOrigin = apiOriginForStaticAssets();
  const photoSrc = employee.photo_url ? `${serverOrigin}${employee.photo_url}` : undefined;
  const pendingPcr = profileRequests.filter((r) =>
    ['Pending', 'PendingApproval'].includes(String(r.status))
  ).length;
  const currentAddr = parseAddress(employee.current_address_json);
  const permanentAddr = parseAddress(employee.permanent_address_json);

  const ecColumns: ColumnsType<EmergencyContactRecord> = [
    { title: 'Name', dataIndex: 'contact_name' },
    { title: 'Relationship', dataIndex: 'relationship' },
    { title: 'Primary phone', dataIndex: 'phone_primary' },
    { title: 'Secondary', dataIndex: 'phone_secondary', render: (v) => v || '—' },
    { title: 'Email', dataIndex: 'email', render: (v) => v || '—' },
    {
      title: 'Primary',
      dataIndex: 'is_primary',
      render: (p: boolean) => (p ? <Tag color="blue">Yes</Tag> : '—'),
    },
  ];
  if (canEdit) {
    ecColumns.push({
      title: '',
      key: 'a',
      render: (_: unknown, r: EmergencyContactRecord) => (
        <Button type="link" size="small" onClick={() => openEc(r)}>
          Edit
        </Button>
      ),
    });
  }

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Employee"
          documentNumber={employee.employee_code}
          statusTag={
            <Space>
              <Tag color={hrmsStatusColor(employee.employment_status)}>{employee.employment_status}</Tag>
              <Tag color="geekblue">v{employee.profile_version ?? 1}</Tag>
              {pendingPcr > 0 && <Tag color="orange">{pendingPcr} pending change(s)</Tag>}
            </Space>
          }
          subtitle={
            <Text type="secondary">
              {employee.first_name} {employee.last_name}
              {employee.job_title ? ` · ${employee.job_title}` : ''} · {employee.email}
            </Text>
          }
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/employees')}>
                Back
              </Button>
              {canEdit && (
                <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/hr/employees/${id}/edit`)}>
                  Propose profile change
                </Button>
              )}
              <Button icon={<HistoryOutlined />} onClick={() => navigate('/hr/salary-revisions/new')}>
                New salary revision
              </Button>
              {canViewCompetency && (
                <Button icon={<SafetyCertificateOutlined />} onClick={() => navigate(`/grc/competency/employees/${id}`)}>
                  Competency & training
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      {id ? <AiPageContextSync module="hr" docType="Employee" docId={id} /> : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Version-controlled employee master"
        description={
          <span>
            After an employee leaves Onboarding, profile edits create an approval request. Compensation is never edited
            here — use <Link to="/hr/salary-revisions">Salary revisions</Link>. Attach{' '}
            {indiaPack
              ? 'India statutory documents (PAN, Aadhaar, UAN/PF, ESI…)'
              : 'UAE documents (Passport, Visa, Emirates ID, Labour card…)'}{' '}
            on the Documents tab. Pending changes:{' '}
            <Link to="/hr/profile-change-requests">Profile change requests</Link>.
          </span>
        }
      />

      <Tabs
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Card title="Photo" style={{ marginBottom: 16 }}>
                    <Space direction="vertical" align="center" style={{ width: '100%' }}>
                      <Avatar size={120} src={photoSrc} icon={<UserOutlined />} />
                      {canEdit && (
                        <Upload
                          showUploadList={false}
                          beforeUpload={(file) => {
                            photoMutation.mutate(file);
                            return false;
                          }}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                        >
                          <Button icon={<UploadOutlined />} loading={photoMutation.isPending}>
                            Upload photo
                          </Button>
                        </Upload>
                      )}
                    </Space>
                  </Card>
                  <Card title="Current compensation (read-only)" size="small" style={{ marginBottom: 16 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Basic">
                        {employee.currency || 'AED'}{' '}
                        {Number(employee.basic_salary ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </Descriptions.Item>
                      <Descriptions.Item label="Source">Applied via salary revisions only</Descriptions.Item>
                    </Descriptions>
                  </Card>
                  <Card title="Quick links" size="small">
                    <Space direction="vertical">
                      <Link to="/hr/salary-revisions">Salary revisions</Link>
                      <Link to="/hr/leaves">Leave applications</Link>
                      <Link to="/hr/contracts">Employment contracts</Link>
                      <Link to="/hr/profile-change-requests">Profile change requests</Link>
                      <a onClick={() => navigate(`/hr/employees/${id}/edit`)}>Propose profile change</a>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} md={16}>
                  <Card title="Employment summary" style={{ marginBottom: 16 }}>
                    <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                      <Descriptions.Item label="Employee ID">{employee.employee_code || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Job title">{employee.job_title || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Work location">{locationLabel(employee.location_id)}</Descriptions.Item>
                      <Descriptions.Item label="Grade">{employee.grade || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Type">{employee.employment_type || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Status">{employee.employment_status || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Joined">{fmtDate(employee.date_of_joining)}</Descriptions.Item>
                      <Descriptions.Item label="Left">{fmtDate(employee.date_of_leaving)}</Descriptions.Item>
                      <Descriptions.Item label="Mobile">{employee.mobile || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Nationality">{employee.nationality || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Profile version">{employee.profile_version ?? 1}</Descriptions.Item>
                      <Descriptions.Item label="Bank">{employee.bank_name || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Default shift">
                        {employee.default_shift_id
                          ? shiftLabelById.get(employee.default_shift_id) || employee.default_shift_id
                          : '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Holiday calendar">
                        {employee.holiday_calendar_id
                          ? calendarLabelById.get(employee.holiday_calendar_id) || employee.holiday_calendar_id
                          : '—'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                  <Card title="Compliance watch">
                    {complianceItems.length === 0 ? (
                      <Text type="secondary">
                        No expiry dates on file — add IDs on profile change and upload scans.
                      </Text>
                    ) : (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {complianceItems.map((c) => (
                          <Space key={c.label}>
                            <Text strong>{c.label}</Text>
                            <Text>{fmtDate(c.value)}</Text>
                            {c.days != null && (
                              <Tag color={c.days < 0 ? 'red' : c.days <= 90 ? 'orange' : 'green'}>
                                {c.days < 0 ? 'Expired' : `${c.days} days`}
                              </Tag>
                            )}
                          </Space>
                        ))}
                      </Space>
                    )}
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'personal',
            label: 'Personal',
            children: (
              <Card title="Personal demographics & contact">
                <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                  <Descriptions.Item label="First name">{employee.first_name}</Descriptions.Item>
                  <Descriptions.Item label="Last name">{employee.last_name}</Descriptions.Item>
                  <Descriptions.Item label="Middle name">{employee.middle_name || '—'}</Descriptions.Item>
                  {!indiaPack && (
                    <Descriptions.Item label="Arabic name">{employee.name_ar || '—'}</Descriptions.Item>
                  )}
                  <Descriptions.Item label="Gender">{employee.gender || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Date of birth">{fmtDate(employee.date_of_birth)}</Descriptions.Item>
                  <Descriptions.Item label="Nationality">{employee.nationality || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Marital status">{employee.marital_status || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Religion">{employee.religion || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Blood group">{employee.blood_group || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Country of birth">{employee.country_of_birth || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Place of birth">{employee.place_of_birth || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Work email">{employee.email}</Descriptions.Item>
                  <Descriptions.Item label="Personal email">{employee.personal_email || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Linked login" span={2}>
                    {employee.user_id ? (
                      <Text type="success">Linked (My HRMS enabled)</Text>
                    ) : (
                      <Text type="warning">Not linked — set Linked login user on Edit employee</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mobile">{employee.mobile || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Home phone">{employee.home_phone || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Alternate phone">{employee.alternate_phone || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Address notes">{employee.address || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Current address" span={2}>
                    {currentAddr
                      ? [currentAddr.line1, currentAddr.city, currentAddr.country].filter(Boolean).join(', ') || '—'
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Permanent address" span={2}>
                    {permanentAddr
                      ? [permanentAddr.line1, permanentAddr.city, permanentAddr.country]
                          .filter(Boolean)
                          .join(', ') || '—'
                      : '—'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'employment',
            label: 'Employment',
            children: (
              <>
                <Card
                  title="Job & contract fields"
                  style={{ marginBottom: 16 }}
                  extra={
                    canEdit && (
                      <Space wrap>
                        {(employee.confirmation_status === 'Pending' ||
                          (!employee.confirmation_date &&
                            employee.employment_status !== 'Terminated' &&
                            employee.employment_status !== 'Exited')) && (
                          <Button
                            loading={lifecycleBusy === 'confirm'}
                            onClick={async () => {
                              setLifecycleBusy('confirm');
                              try {
                                await hrmsService.confirmEmployee(id!, {
                                  confirmation_date: dayjs().format('YYYY-MM-DD'),
                                });
                                message.success('Employee confirmed');
                                queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id!) });
                              } catch {
                                message.error('Confirm failed');
                              } finally {
                                setLifecycleBusy(null);
                              }
                            }}
                          >
                            Confirm employee
                          </Button>
                        )}
                        {employee.employment_status !== 'Terminated' &&
                          employee.employment_status !== 'Exited' && (
                            <Button
                              danger
                              onClick={() => {
                                exitForm.setFieldsValue({
                                  exit_type: 'Resignation',
                                  termination_date: dayjs(),
                                });
                                setExitModalOpen(true);
                              }}
                            >
                              Start exit
                            </Button>
                          )}
                      </Space>
                    )
                  }
                >
                  <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                    <Descriptions.Item label="Employee ID">{employee.employee_code || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Job title">{employee.job_title || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Grade">{employee.grade || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Department">
                      {(employee.department_id && departmentLabelById.get(employee.department_id)) || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Reports to">
                      {(employee.reporting_to_id && employeeLabelById.get(employee.reporting_to_id)) || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cost center">{employee.cost_center_code || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Branch">
                      {(employee.branch_id && branchLabelById.get(employee.branch_id)) || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Work location">{locationLabel(employee.location_id)}</Descriptions.Item>
                    <Descriptions.Item label="Joined">{fmtDate(employee.date_of_joining)}</Descriptions.Item>
                    <Descriptions.Item label="Left">{fmtDate(employee.date_of_leaving)}</Descriptions.Item>
                    <Descriptions.Item label="Employment type">{employee.employment_type || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Employment status">{employee.employment_status || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Contract type">{employee.contract_type || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Contract start">{fmtDate(employee.contract_start_date)}</Descriptions.Item>
                    <Descriptions.Item label="Contract end">{fmtDate(employee.contract_end_date)}</Descriptions.Item>
                    <Descriptions.Item label="Probation (months)">
                      {employee.probation_months ?? '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Confirmation status">
                      {employee.confirmation_status ||
                        (employee.confirmation_date ? 'Confirmed' : 'Pending')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Confirmation">{fmtDate(employee.confirmation_date)}</Descriptions.Item>
                    <Descriptions.Item label="Notice period (days)">
                      {employee.notice_period_days ?? '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last duty resume">
                      {fmtDate(employee.last_duty_resume_date)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Termination date">{fmtDate(employee.termination_date)}</Descriptions.Item>
                    <Descriptions.Item label="Termination reason">{employee.termination_reason || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Rehire eligible">
                      {employee.rehire_eligible == null ? '—' : employee.rehire_eligible ? 'Yes' : 'No'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
                <Card
                  title="Linked contracts"
                  extra={
                    <Button onClick={() => navigate('/hr/contracts')}>Open contracts</Button>
                  }
                >
                  <Table
                    rowKey="id"
                    dataSource={contracts}
                    pagination={{ pageSize: 8 }}
                    columns={[
                      {
                        title: 'Contract #',
                        dataIndex: 'contract_number',
                        render: (v: string, r: any) => (
                          <a onClick={() => navigate(`/hr/contracts/${r.id}`)}>{v || 'Open'}</a>
                        ),
                      },
                      { title: 'Type', dataIndex: 'contract_type', render: (v) => v || '—' },
                      {
                        title: 'Start',
                        dataIndex: 'effective_from',
                        render: (d: string) => fmtDate(d),
                      },
                      {
                        title: 'End',
                        dataIndex: 'effective_to',
                        render: (d: string) => fmtDate(d),
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                      },
                    ]}
                    locale={{ emptyText: 'No linked contracts' }}
                  />
                </Card>
                <Modal
                  title="Start employee exit"
                  open={exitModalOpen}
                  onCancel={() => setExitModalOpen(false)}
                  confirmLoading={lifecycleBusy === 'exit'}
                  onOk={async () => {
                    try {
                      const v = await exitForm.validateFields();
                      setLifecycleBusy('exit');
                      const exited = await hrmsService.exitEmployee(id!, {
                        exit_type: v.exit_type,
                        termination_date: v.termination_date.format('YYYY-MM-DD'),
                        termination_reason: v.termination_reason,
                      });
                      message.success('Exit started');
                      setExitModalOpen(false);
                      promptHandoverIfNeeded({
                        summary: exited?.responsibility_summary,
                        userId: exited?.user_id,
                        personLabel: exited?.full_name || 'This employee',
                        reason: /terminat/i.test(String(v.exit_type)) ? 'termination' : 'resignation',
                        navigate,
                      });
                      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id!) });
                    } catch (e: any) {
                      if (e?.errorFields) return;
                      message.error(e?.message || 'Exit failed');
                    } finally {
                      setLifecycleBusy(null);
                    }
                  }}
                  destroyOnClose
                >
                  <Form form={exitForm} layout="vertical">
                    <Form.Item name="exit_type" label="Exit type" rules={[{ required: true }]}>
                      <Select options={EXIT_TYPE_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="termination_date" label="Termination date" rules={[{ required: true }]}>
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="termination_reason" label="Reason">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                  </Form>
                </Modal>
                {canViewAllocations && (
                  <Card
                    title="Project allocations"
                    style={{ marginTop: 16 }}
                    extra={
                      <Link to={`/hr/allocations?employee=${id}`}>Manage allocations</Link>
                    }
                  >
                    <Table<AllocationRow>
                      rowKey="id"
                      size="small"
                      dataSource={allocations}
                      pagination={allocations.length > 10 ? { pageSize: 10 } : false}
                      locale={{ emptyText: 'Not allocated to any project' }}
                      columns={[
                        {
                          title: 'Project',
                          key: 'project',
                          render: (_, r) => `${r.project_code}${r.project_name ? ` — ${r.project_name}` : ''}`,
                        },
                        { title: 'Role', dataIndex: 'role_on_project', width: 140, render: (v) => v || '—' },
                        { title: '%', dataIndex: 'allocation_pct', width: 60, align: 'right' },
                        {
                          title: 'From',
                          dataIndex: 'effective_from',
                          width: 115,
                          render: (d: string) => dayjs(d).format('DD MMM YYYY'),
                        },
                        {
                          title: 'To',
                          dataIndex: 'effective_to',
                          width: 115,
                          render: (d: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : 'Open-ended'),
                        },
                        {
                          title: 'Status',
                          dataIndex: 'display_status',
                          width: 100,
                          render: (s: string) => <Tag color={ALLOCATION_STATUS_COLOR[s]}>{s}</Tag>,
                        },
                      ]}
                    />
                  </Card>
                )}
              </>
            ),
          },
          indiaPack
            ? {
                key: 'statutory',
                label: 'Statutory ID',
                children: (
                  <Card title="India statutory identity">
                    <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                      <Descriptions.Item label="PAN">{employee.pan_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Tax regime">{employee.tax_regime || '—'}</Descriptions.Item>
                      <Descriptions.Item label="UAN (EPFO)">{employee.uan_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="ESI number">{employee.esi_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Aadhaar / National ID">{employee.emirates_id || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Passport number">{employee.passport_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Passport issue">{fmtDate(employee.passport_issue_date)}</Descriptions.Item>
                      <Descriptions.Item label="Passport expiry">{fmtDate(employee.passport_expiry_date)}</Descriptions.Item>
                      <Descriptions.Item label="Place of issue" span={2}>
                        {employee.passport_place_of_issue || '—'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              }
            : {
                key: 'immigration',
                label: 'Immigration',
                children: (
                  <Card title="UAE immigration & labour">
                    <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                      <Descriptions.Item label="Emirates ID">{employee.emirates_id || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Emirates ID expiry">{fmtDate(employee.emirates_id_expiry)}</Descriptions.Item>
                      <Descriptions.Item label="Passport number">{employee.passport_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Passport issue">{fmtDate(employee.passport_issue_date)}</Descriptions.Item>
                      <Descriptions.Item label="Passport expiry">{fmtDate(employee.passport_expiry_date)}</Descriptions.Item>
                      <Descriptions.Item label="Place of issue">{employee.passport_place_of_issue || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Visa number">{employee.visa_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Visa type">{employee.visa_type || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Visa issue">{fmtDate(employee.visa_issue_date)}</Descriptions.Item>
                      <Descriptions.Item label="Visa expiry">{fmtDate(employee.visa_expiry_date)}</Descriptions.Item>
                      <Descriptions.Item label="Visa sponsor">{employee.visa_sponsor || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Visa status">{employee.visa_status || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Labour card #">{employee.labour_card_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Labour card expiry">{fmtDate(employee.labour_card_expiry)}</Descriptions.Item>
                      <Descriptions.Item label="Labour job title">{employee.labour_card_job_title || '—'}</Descriptions.Item>
                      <Descriptions.Item label="UID number">{employee.uid_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Work permit #">{employee.work_permit_number || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Work permit issue">{fmtDate(employee.work_permit_issue_date)}</Descriptions.Item>
                      <Descriptions.Item label="Work permit expiry" span={2}>
                        {fmtDate(employee.work_permit_expiry_date)}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              },
          {
            key: 'payroll',
            label: 'Payroll & bank',
            children: (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="No salary entry on employee"
                  description={
                    <span>
                      Create and approve a <Link to="/hr/salary-revisions/new">salary revision</Link> to change basic or
                      components. This tab is history only.
                    </span>
                  }
                />
                <Card title={indiaPack ? 'Bank & NEFT' : 'Bank & WPS'} style={{ marginBottom: 16 }}>
                  <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                    <Descriptions.Item label="Bank">{employee.bank_name || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Account">{employee.bank_account_number || '—'}</Descriptions.Item>
                    <Descriptions.Item label="IBAN">{employee.iban || '—'}</Descriptions.Item>
                    {!indiaPack && (
                      <Descriptions.Item label="WPS person code">{employee.wps_person_code || '—'}</Descriptions.Item>
                    )}
                    <Descriptions.Item label="Payment method">{employee.payment_method || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Currency">{employee.currency || '—'}</Descriptions.Item>
                    <Descriptions.Item label="SWIFT">{employee.swift_code || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Branch name">{employee.bank_branch_name || '—'}</Descriptions.Item>
                    <Descriptions.Item label={indiaPack ? 'IFSC' : 'Branch code'}>
                      {employee.bank_branch_code || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Basic (read-only)">
                      {employee.currency || (indiaPack ? 'INR' : 'AED')}{' '}
                      {Number(employee.basic_salary ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
                <Card
                  title="Salary revisions"
                  style={{ marginBottom: 16 }}
                  extra={
                    <Button type="primary" onClick={() => navigate('/hr/salary-revisions/new')}>
                      New revision
                    </Button>
                  }
                >
                  <Table
                    rowKey="id"
                    dataSource={salaryRevisions}
                    pagination={{ pageSize: 8 }}
                    columns={[
                      {
                        title: 'Revision #',
                        dataIndex: 'revision_number',
                        render: (v: string, r: any) => (
                          <a onClick={() => navigate(`/hr/salary-revisions/${r.id}`)}>{v || 'Open'}</a>
                        ),
                      },
                      {
                        title: 'Effective',
                        dataIndex: 'effective_from',
                        render: (d: string) => fmtDate(d),
                      },
                      { title: 'Previous basic', dataIndex: 'previous_basic' },
                      { title: 'New basic', dataIndex: 'new_basic' },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                      },
                    ]}
                    locale={{ emptyText: 'No salary revisions yet' }}
                  />
                </Card>
                <Card title="Current salary components (read-only)">
                  <Table<SalaryComponentRecord>
                    rowKey="id"
                    dataSource={salaryComponents}
                    pagination={false}
                    columns={[
                      { title: 'Code', dataIndex: 'component_code' },
                      { title: 'Name', dataIndex: 'component_name' },
                      { title: 'Amount', dataIndex: 'amount' },
                      {
                        title: 'From',
                        dataIndex: 'effective_from',
                        render: (d: string) => fmtDate(d),
                      },
                      {
                        title: 'To',
                        dataIndex: 'effective_to',
                        render: (t: string) => fmtDate(t),
                      },
                    ]}
                    locale={{ emptyText: 'No components — applied when a salary revision is approved' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'schedule',
            label: 'Schedule',
            children: (
              <>
                <Card title="Default schedule" style={{ marginBottom: 16 }}>
                  <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                    <Descriptions.Item label="Default shift">
                      {employee.default_shift_id
                        ? shiftLabelById.get(employee.default_shift_id) || employee.default_shift_id
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Holiday calendar">
                      {employee.holiday_calendar_id
                        ? calendarLabelById.get(employee.holiday_calendar_id) || employee.holiday_calendar_id
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Work schedule">{employee.work_schedule || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Shift type">{employee.shift_type || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Exclude attendance rules" span={2}>
                      {employee.exclude_from_attendance_rules ? 'Yes' : 'No'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
                <Card
                  title="Shift assignments"
                  extra={
                    canEdit && (
                      <Space>
                        <Button onClick={() => navigate('/hr/employees/shift-assignment')}>Bulk shift assignment</Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          shiftForm.resetFields();
                          setShiftModalOpen(true);
                        }}
                      >
                        Add assignment
                      </Button>
                      </Space>
                    )
                  }
                >
                  <Table<EmployeeShiftAssignmentRecord>
                    rowKey="id"
                    dataSource={shiftAssignments}
                    pagination={false}
                    columns={[
                      {
                        title: 'Shift',
                        dataIndex: 'shift_id',
                        render: (sid: string) => shiftLabelById.get(sid) || sid,
                      },
                      {
                        title: 'From',
                        dataIndex: 'effective_from',
                        render: (d: string) => fmtDate(d),
                      },
                      {
                        title: 'To',
                        dataIndex: 'effective_to',
                        render: (d: string) => fmtDate(d),
                      },
                      ...(canEdit
                        ? [
                            {
                              title: '',
                              key: 'actions',
                              render: (_: unknown, r: EmployeeShiftAssignmentRecord) => {
                                const upcoming = dayjs(r.effective_from).isAfter(dayjs(), 'day');
                                const open = !r.effective_to || dayjs(r.effective_to).isAfter(dayjs(), 'day');
                                if (!upcoming && !open) return '—';
                                return (
                                  <Popconfirm
                                    title={upcoming ? 'Cancel this upcoming assignment?' : 'End this assignment today?'}
                                    onConfirm={() => endShift(r)}
                                  >
                                    <Button type="link" size="small">
                                      {upcoming ? 'Cancel' : 'End'}
                                    </Button>
                                  </Popconfirm>
                                );
                              },
                            } as ColumnsType<EmployeeShiftAssignmentRecord>[number],
                          ]
                        : []),
                    ]}
                    locale={{ emptyText: 'No shift assignments' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'leave',
            label: 'Leave',
            children: (
              <>
                <Card
                  title="Leave balances"
                  style={{ marginBottom: 16 }}
                  extra={
                    canEdit && (
                      <Space>
                        <Button onClick={() => navigate('/hr/leave-opening-balances')}>
                          Go-live openings
                        </Button>
                        <Button type="primary" onClick={() => setLbModalOpen(true)}>
                          Add / update
                        </Button>
                      </Space>
                    )
                  }
                >
                  <Table<LeaveBalanceRecord>
                    rowKey="id"
                    dataSource={leaveBalances}
                    pagination={false}
                    columns={[
                      { title: 'Year', dataIndex: 'leave_year' },
                      { title: 'Type', dataIndex: 'leave_type' },
                      { title: 'Opening', dataIndex: 'opening_balance' },
                      { title: 'Accrued', dataIndex: 'accrued', render: (v) => Number(v || 0) },
                      { title: 'Carried', dataIndex: 'carried_forward' },
                      { title: 'Consumed', dataIndex: 'consumed' },
                      { title: 'Pending', dataIndex: 'pending', render: (v) => Number(v || 0) },
                      { title: 'Encashed', dataIndex: 'encashed', render: (v) => Number(v || 0) },
                      {
                        title: 'Available',
                        dataIndex: 'available_days',
                        render: (v, r) =>
                          v ??
                          Number(r.opening_balance || 0) +
                            Number(r.accrued || 0) +
                            Number(r.carried_forward || 0) -
                            Number(r.consumed || 0) -
                            Number(r.encashed || 0) -
                            Number(r.pending || 0),
                      },
                    ]}
                  />
                </Card>
                <Card
                  title="Leave applications"
                  extra={<Button onClick={() => navigate('/hr/leaves')}>Open leave list</Button>}
                >
                  <Table
                    rowKey="id"
                    dataSource={leaveApps}
                    pagination={{ pageSize: 8 }}
                    columns={[
                      {
                        title: 'Application',
                        dataIndex: 'leave_application_number',
                        render: (v: string, r: any) => (
                          <a onClick={() => navigate(`/hr/leaves/${r.id}`)}>{v || 'Open'}</a>
                        ),
                      },
                      { title: 'Type', dataIndex: 'leave_type', render: (v) => v || '—' },
                      {
                        title: 'From',
                        dataIndex: 'from_date',
                        render: (d: string) => fmtDate(d),
                      },
                      {
                        title: 'To',
                        dataIndex: 'to_date',
                        render: (d: string) => fmtDate(d),
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                      },
                    ]}
                    locale={{ emptyText: 'No leave applications' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            children: <EmployeeUaeDocumentsCard employeeId={id} readOnly={!canEdit} />,
          },
          {
            key: 'qualifications',
            label: 'Qualifications',
            children: (
              <Card
                title="Qualifications & credentials"
                extra={
                  canEdit && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openQual()}>
                      Add
                    </Button>
                  )
                }
              >
                <Table<EmployeeQualificationRecord>
                  rowKey="id"
                  dataSource={qualifications}
                  pagination={{ pageSize: 10 }}
                  columns={[
                    { title: 'Type', dataIndex: 'qual_type' },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Institution', dataIndex: 'institution', render: (v) => v || '—' },
                    { title: 'Credential #', dataIndex: 'credential_number', render: (v) => v || '—' },
                    {
                      title: 'Issued',
                      dataIndex: 'issued_on',
                      render: (d: string) => fmtDate(d),
                    },
                    {
                      title: 'Expiry',
                      dataIndex: 'expiry_date',
                      render: (d: string) => fmtDate(d),
                    },
                    { title: 'Level', dataIndex: 'level_or_grade', render: (v) => v || '—' },
                    {
                      title: 'Verified',
                      dataIndex: 'is_verified',
                      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : '—'),
                    },
                    ...(canEdit
                      ? [
                          {
                            title: '',
                            key: 'actions',
                            width: 120,
                            render: (_: unknown, r: EmployeeQualificationRecord) => (
                              <Space>
                                <Button type="link" size="small" onClick={() => openQual(r)}>
                                  Edit
                                </Button>
                                <Popconfirm
                                  title="Delete qualification?"
                                  onConfirm={async () => {
                                    await employeeService.deleteQualification(id!, r.id);
                                    message.success('Deleted');
                                    queryClient.invalidateQueries({
                                      queryKey: employeeKeys.qualifications(id!),
                                    });
                                  }}
                                >
                                  <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Space>
                            ),
                          } as ColumnsType<EmployeeQualificationRecord>[number],
                        ]
                      : []),
                  ]}
                  locale={{ emptyText: 'No qualifications recorded' }}
                />
              </Card>
            ),
          },
          {
            key: 'performance',
            label: 'Performance',
            children: (
              <Card
                title="Performance records"
                extra={
                  canEdit && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openPerf()}>
                      Add
                    </Button>
                  )
                }
              >
                <Table<EmployeePerformanceRecord>
                  rowKey="id"
                  dataSource={performance}
                  pagination={{ pageSize: 10 }}
                  columns={[
                    { title: 'Type', dataIndex: 'record_type' },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Year', dataIndex: 'period_year', render: (v) => v ?? '—' },
                    { title: 'Period', dataIndex: 'period_label', render: (v) => v || '—' },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                    },
                    { title: 'Rating', dataIndex: 'rating', render: (v) => v || '—' },
                    {
                      title: 'Due',
                      dataIndex: 'due_date',
                      render: (d: string) => fmtDate(d),
                    },
                    {
                      title: 'Completed',
                      dataIndex: 'completed_date',
                      render: (d: string) => fmtDate(d),
                    },
                    ...(canEdit
                      ? [
                          {
                            title: '',
                            key: 'actions',
                            width: 120,
                            render: (_: unknown, r: EmployeePerformanceRecord) => (
                              <Space>
                                <Button type="link" size="small" onClick={() => openPerf(r)}>
                                  Edit
                                </Button>
                                <Popconfirm
                                  title="Delete record?"
                                  onConfirm={async () => {
                                    await employeeService.deletePerformance(id!, r.id);
                                    message.success('Deleted');
                                    queryClient.invalidateQueries({
                                      queryKey: employeeKeys.performance(id!),
                                    });
                                  }}
                                >
                                  <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Space>
                            ),
                          } as ColumnsType<EmployeePerformanceRecord>[number],
                        ]
                      : []),
                  ]}
                  locale={{ emptyText: 'No performance records' }}
                />
              </Card>
            ),
          },
          {
            key: 'hse',
            label: 'HSE / Insurance',
            children: (
              <Card title="Insurance & medical">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="HSE and medical document scans belong on the Documents tab"
                />
                <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
                  <Descriptions.Item label="Insurance provider">{employee.insurance_provider || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Policy number">{employee.insurance_policy_number || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Insurance expiry">{fmtDate(employee.insurance_expiry)}</Descriptions.Item>
                  <Descriptions.Item label="Blood group">{employee.blood_group || '—'}</Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'lifecycle',
            label: 'Lifecycle',
            children: (
              <Card
                title="Lifecycle events"
                extra={
                  canEdit && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openLife()}>
                      Add event
                    </Button>
                  )
                }
              >
                <Table<EmployeeLifecycleEventRecord>
                  rowKey="id"
                  dataSource={lifecycle}
                  pagination={{ pageSize: 10 }}
                  columns={[
                    { title: 'Type', dataIndex: 'event_type' },
                    {
                      title: 'Event date',
                      dataIndex: 'event_date',
                      render: (d: string) => fmtDate(d),
                    },
                    {
                      title: 'Effective from',
                      dataIndex: 'effective_from',
                      render: (d: string) => fmtDate(d),
                    },
                    {
                      title: 'Effective to',
                      dataIndex: 'effective_to',
                      render: (d: string) => fmtDate(d),
                    },
                    {
                      title: 'Location',
                      key: 'location',
                      render: (_: unknown, r: EmployeeLifecycleEventRecord) =>
                        r.from_location_id || r.to_location_id
                          ? `${locationLabel(r.from_location_id)} → ${locationLabel(r.to_location_id)}`
                          : '—',
                    },
                    { title: 'From job', dataIndex: 'from_job_title', render: (v) => v || '—' },
                    { title: 'To job', dataIndex: 'to_job_title', render: (v) => v || '—' },
                    { title: 'From grade', dataIndex: 'from_grade', render: (v) => v || '—' },
                    { title: 'To grade', dataIndex: 'to_grade', render: (v) => v || '—' },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                    },
                    { title: 'Reason', dataIndex: 'reason', ellipsis: true, render: (v) => v || '—' },
                    ...(canEdit
                      ? [
                          {
                            title: '',
                            key: 'actions',
                            width: 120,
                            render: (_: unknown, r: EmployeeLifecycleEventRecord) => (
                              <Space>
                                <Button type="link" size="small" onClick={() => openLife(r)}>
                                  Edit
                                </Button>
                                <Popconfirm
                                  title="Delete event?"
                                  onConfirm={async () => {
                                    await employeeService.deleteLifecycle(id!, r.id);
                                    message.success('Deleted');
                                    queryClient.invalidateQueries({
                                      queryKey: employeeKeys.lifecycle(id!),
                                    });
                                  }}
                                >
                                  <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Space>
                            ),
                          } as ColumnsType<EmployeeLifecycleEventRecord>[number],
                        ]
                      : []),
                  ]}
                  locale={{ emptyText: 'No lifecycle events' }}
                />
              </Card>
            ),
          },
          {
            key: 'emergency',
            label: 'Emergency',
            children: (
              <Card
                title="Emergency contacts"
                extra={
                  canEdit && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openEc()}>
                      Add contact
                    </Button>
                  )
                }
              >
                <Table rowKey="id" columns={ecColumns} dataSource={emergency} pagination={false} />
              </Card>
            ),
          },
          {
            key: 'versions',
            label: 'Profile versions',
            children: (
              <Card title="Profile change history">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Each approved change increments the employee profile version"
                  description={
                    <span>
                      Review the company queue at <Link to="/hr/profile-change-requests">Profile change requests</Link>.
                    </span>
                  }
                />
                <Table<ProfileChangeRequestRecord>
                  rowKey="id"
                  dataSource={profileRequests}
                  pagination={{ pageSize: 10 }}
                  columns={[
                    {
                      title: 'When',
                      dataIndex: 'created_at',
                      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY HH:mm') : '—'),
                    },
                    { title: 'Scope', dataIndex: 'field_key', width: 120 },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      width: 130,
                      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                    },
                    {
                      title: 'Changes',
                      key: 'diff',
                      ellipsis: true,
                      render: (_, r) => {
                        try {
                          const n = JSON.parse(r.new_value_json || '{}');
                          return Object.keys(n).join(', ') || r.field_key;
                        } catch {
                          return r.field_key;
                        }
                      },
                    },
                    {
                      title: 'Reviewed',
                      dataIndex: 'reviewed_at',
                      width: 140,
                      render: (d: string) => fmtDate(d),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={ecEditing ? 'Edit emergency contact' : 'Add emergency contact'}
        open={ecModalOpen}
        onCancel={() => setEcModalOpen(false)}
        onOk={saveEmergency}
        destroyOnClose
      >
        <Form form={ecForm} layout="vertical">
          <Form.Item name="contact_name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="relationship" label="Relationship" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone_primary" label="Primary phone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone_secondary" label="Secondary phone">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_primary" label="Primary contact" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Leave balance" open={lbModalOpen} onCancel={() => setLbModalOpen(false)} onOk={submitLeaveBalance} destroyOnClose>
        <Form form={lbForm} layout="vertical" initialValues={{ leave_year: dayjs().year(), leave_type: 'ANNUAL' }}>
          <Form.Item name="leave_year" label="Year" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="leave_type" label="Type" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="opening_balance" label="Opening">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="consumed" label="Consumed">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="carried_forward" label="Carried forward">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={qualEditing ? 'Edit qualification' : 'Add qualification'}
        open={qualModalOpen}
        onCancel={() => setQualModalOpen(false)}
        onOk={saveQual}
        destroyOnClose
        width={640}
      >
        <Form form={qualForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="qual_type" label="Type" rules={[{ required: true }]}>
                <Select>
                  {QUAL_TYPES.map((t) => (
                    <Select.Option key={t} value={t}>
                      {t}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="institution" label="Institution">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="credential_number" label="Credential number">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="issued_on" label="Issued on">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiry_date" label="Expiry">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="level_or_grade" label="Level / grade">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="country" label="Country">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_verified" label="Verified" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={perfEditing ? 'Edit performance record' : 'Add performance record'}
        open={perfModalOpen}
        onCancel={() => setPerfModalOpen(false)}
        onOk={savePerf}
        destroyOnClose
        width={640}
      >
        <Form form={perfForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="record_type" label="Type" rules={[{ required: true }]}>
                <Select>
                  {PERF_TYPES.map((t) => (
                    <Select.Option key={t} value={t}>
                      {t}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="period_year" label="Period year">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="period_label" label="Period label">
                <Input placeholder="H1, Q3…" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="Open">Open</Select.Option>
                  <Select.Option value="In Progress">In Progress</Select.Option>
                  <Select.Option value="Completed">Completed</Select.Option>
                  <Select.Option value="Cancelled">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rating" label="Rating">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="completed_date" label="Completed date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="summary" label="Summary">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={lifeEditing ? 'Edit lifecycle event' : 'Add lifecycle event'}
        open={lifeModalOpen}
        onCancel={() => setLifeModalOpen(false)}
        onOk={saveLife}
        destroyOnClose
        width={720}
      >
        <Form form={lifeForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="event_type" label="Event type" rules={[{ required: true }]}>
                <Select>
                  {LIFECYCLE_TYPES.map((t) => (
                    <Select.Option key={t} value={t}>
                      {t}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="event_date" label="Event date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effective_from" label="Effective from">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effective_to" label="Effective to">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="from_job_title" label="From job title">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="to_job_title" label="To job title">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="from_grade" label="From grade">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="to_grade" label="To grade">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="Recorded">Recorded</Select.Option>
                  <Select.Option value="Pending">Pending</Select.Option>
                  <Select.Option value="Completed">Completed</Select.Option>
                  <Select.Option value="Cancelled">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="document_ref" label="Document ref">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="reason" label="Reason">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="outcome" label="Outcome">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Add shift assignment"
        open={shiftModalOpen}
        onCancel={() => setShiftModalOpen(false)}
        onOk={saveShiftAssignment}
        destroyOnClose
      >
        <Form form={shiftForm} layout="vertical">
          <Form.Item name="shift_id" label="Shift" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select shift"
            >
              {shifts.map((s: any) => (
                <Select.Option key={s.id} value={s.id}>
                  {s.shift_name || s.shift_code || 'Shift'}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective to">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default EmployeeProfilePage;
